import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { getBrand } from '../../constants/brands';
import { applyBrandNames } from '../../constants/brands/policyText';
import { policySanitizeSchema, rehypeBlankLinkRel } from './policySanitize';

export const MarkdownContent = styled.div`
  ${tw`prose prose-lg max-w-none`}
  scroll-behavior: smooth;

  h1 {
    ${tw`text-3xl font-bold mt-8 mb-4 text-text-primary`}
    scroll-margin-top: 80px;
  }

  h2 {
    ${tw`text-2xl font-bold mt-6 mb-3 text-text-primary`}
    scroll-margin-top: 80px;
  }

  h3 {
    ${tw`text-xl font-semibold mt-4 mb-2 text-text-secondary`}
    scroll-margin-top: 80px;
  }

  p {
    ${tw`mb-4 text-text-secondary leading-relaxed`}
  }

  ul,
  ol {
    ${tw`mb-4 ml-6`}
  }

  li {
    ${tw`mb-2 text-text-secondary`}
  }

  strong {
    ${tw`font-semibold text-text-primary`}
  }

  a {
    ${tw`text-black underline`}
    cursor: pointer;
    &:hover {
      ${tw`text-blue-800`}
    }
  }

  blockquote {
    ${tw`border-l-4 border-border-default pl-4 italic my-4 text-text-secondary`}
  }

  code {
    ${tw`bg-bg-secondary px-2 py-1 rounded-md text-sm font-mono`}
  }

  pre {
    ${tw`bg-bg-secondary p-4 rounded-lg overflow-x-auto mb-4`}
  }

  hr {
    ${tw`my-8 border-border-default`}
  }

  /* Fee and refund tables; narrow screens scroll the table, not the page */
  table {
    ${tw`w-full mb-6 text-sm border-collapse`}
    display: block;
    overflow-x: auto;

    @media (min-width: 768px) {
      display: table;
    }
  }

  th,
  td {
    ${tw`border border-border-default px-3 py-2 text-left align-top text-text-secondary`}
    /* No Tailwind preflight in this build, so the border style is not set for us */
    border-style: solid;
  }

  th {
    ${tw`bg-bg-secondary font-semibold text-text-primary`}
  }

  /* Style phone numbers - bold and red */
  .phone-number {
    ${tw`font-bold text-red-600`}
  }

  /* Hide anchor tags but keep them functional */
  a[id] {
    display: block;
    position: relative;
    top: -80px;
    visibility: hidden;
  }

  h1[id],
  h2[id],
  h3[id],
  h4[id],
  h5[id],
  h6[id] {
    position: relative;
  }
`;

/** Replace Turo with the viewing brand and style phone numbers. */
export const processPolicyContent = (content: string): string => {
  let processed = applyBrandNames(content, getBrand());

  // Replace common phone number patterns and wrap them in span with class
  // Pattern: (XXX) XXX-XXXX, XXX-XXX-XXXX, XXX.XXX.XXXX, XXX XXX XXXX, etc.
  // Also handle phone numbers that are already wrapped in spans
  const phonePatterns = [
    /\+\d{1,3}[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g, // International with country code (check first)
    /\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g, // US phone numbers
    /\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/g, // Simple format
    /1-800-[A-Z0-9-]+/gi, // 1-800 style numbers
  ];

  phonePatterns.forEach(pattern => {
    processed = processed.replace(pattern, match => {
      // Skip if already wrapped in a span
      if (match.includes('<span') || match.includes('</span>')) {
        return match;
      }
      return `<span class="phone-number">${match}</span>`;
    });
  });

  return processed;
};

/**
 * Load a policy markdown file (`brandPolicyPath(...)`) for display. Used by the policy
 * pages, the per-trip rental agreement and the host application consent dialog.
 */
export function usePolicyMarkdown(contentPath: string | null, title = 'this document') {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(!!contentPath);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!contentPath) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(contentPath)
      .then(response => {
        if (!response.ok) throw new Error(`Failed to load ${title}`);
        return response.text();
      })
      .then(text => {
        if (!cancelled) setContent(processPolicyContent(text));
      })
      .catch(() => {
        if (!cancelled) setError(`Unable to load ${title}. Please try again later.`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contentPath, title]);

  return { content, loading, error };
}

export const PolicyMarkdown: React.FC<{ content: string }> = ({ content }) => (
  <MarkdownContent>
    <ReactMarkdown
      rehypePlugins={[rehypeRaw, [rehypeSanitize, policySanitizeSchema], rehypeBlankLinkRel]}
      remarkPlugins={[remarkGfm]}
    >
      {content}
    </ReactMarkdown>
  </MarkdownContent>
);
